import { useEffect, useState } from "react";

import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "@mui/material";
import { PageContainer } from "@/features/ui";

import { DeliveryOrderForm } from "@/features/delivery-orders";
import { apiOutletClient, getErrorMessage } from "@/features/shared";
import { CenteredLoading, useSnackbar } from "@/features/ui";

export const EditDeliveryOrder = () => {
  const { referenceNumber } = useParams();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [defaultValues, setDefaultValues] = useState(null);

  useEffect(() => {
    const fetchDeliveryOrder = async () => {
      setIsLoading(true);
      try {
        const {
          data: { data },
        } = await apiOutletClient.get(`delivery-orders/${referenceNumber}`, {
          params: {
            withBuyer: true,
            withSeller: true,
            withWarehouse: true,
            withProductVariants: true,
            withCreator: true,
          },
        });

        setDefaultValues({
          referenceNumber: data.referenceNumber,
          transactionDate: dayjs(data.transactionDate),
          warehouse: data.warehouse,
          buyer: data.buyer,
          buyerableType: data.buyerableType,
          seller: data.seller,
          buyerReferenceNumber: data.buyerReferenceNumber,
          note: data.note,
          deliveryOrderProductVariants: data.deliveryOrderProductVariants.map(
            (variant) => {
              // Get invoiced quantity from SOPV
              const invoicedQuantity = variant.salesOrderProductVariant
                ? variant.salesOrderProductVariant.invoicedQuantity
                : 0;

              // Calculate max quantity: current quantity + remaining available from SOPV
              // maxQuantity = current DOPV qty + (SOPV qty - SOPV deliveredQty)
              const maxQuantity = variant.salesOrderProductVariant
                ? variant.quantity +
                  (variant.salesOrderProductVariant.quantity -
                    variant.salesOrderProductVariant.deliveredQuantity)
                : null;

              // Calculate min quantity: max of returned and invoiced quantities
              const minQuantity = Math.max(
                variant.returnedQuantity,
                invoicedQuantity
              );

              return {
                id: variant.id,
                salesOrderProductVariantId: variant.salesOrderProductVariantId,
                name: variant.name,
                sku: variant.sku,
                unitOfMeasurement: variant.product.unitOfMeasurement.name,
                quantity: variant.quantity,
                note: variant.note,
                returnedQuantity: variant.returnedQuantity,
                invoicedQuantity,
                maxQuantity,
                minQuantity,
                salesOrder: variant.salesOrder,
                salesOrderProductVariant: variant.salesOrderProductVariant,
              };
            }
          ),
        });
      } catch (error) {
        const errorMessage =
          getErrorMessage(error, "Gagal memperbarui pengiriman");
        showSnackbar(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };
    fetchDeliveryOrder();
  }, [referenceNumber, showSnackbar]);

  const handleSubmit = async (formData) => {
    try {
      const { sellerId, ...payload } = formData;

      await apiOutletClient.patch(
        `delivery-orders/${referenceNumber}`,
        payload,
        {
          headers: {
            "Outlet-Id": sellerId,
          },
        }
      );
      showSnackbar("Pengiriman penjualan berhasil diperbarui", "success");
      navigate(-1);
    } catch (error) {
      const errorMessage =
        getErrorMessage(error, "Gagal memperbarui pengiriman");
      showSnackbar(errorMessage);
    }
  };

  if (!defaultValues && isLoading) {
    return <CenteredLoading />;
  }

  return (
    <PageContainer
      maxWidth="xl"
      title="Ubah Pengiriman Penjualan"
      breadcrumbs={[
        { title: "Pengiriman Penjualan", path: "/sales/delivery-orders" },
        {
          title: referenceNumber,
          path: `/sales/delivery-orders/${referenceNumber}`,
        },
        { title: "Ubah" },
      ]}
    >
      <DeliveryOrderForm
        editMode={true}
        isLoading={isLoading}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        renderActionButton={({ isSubmitting }) => (
          <Button type="submit" variant="contained" loading={isSubmitting}>
            Simpan
          </Button>
        )}
      />
    </PageContainer>
  );
};
